import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, Volume2 } from "lucide-react";
import { useAudio } from "@/context/AudioContext";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const AudioDock = () => {
  const {
    current,
    isPlaying,
    hasStarted,
    progress,
    duration,
    volume,
    setVolume,
    togglePlay,
    next,
    prev,
    seek,
  } = useAudio();

  const ratio = duration ? progress / duration : 0;

  return (
    <AnimatePresence>
      {hasStarted && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="audio-dock"
          className="fixed bottom-0 inset-x-0 z-50 px-3 sm:px-6 pb-3 sm:pb-5"
        >
          <div className="mx-auto max-w-5xl rounded-2xl sm:rounded-full border border-gray-200 bg-white/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)]">
            {/* progress bar */}
            <div className="px-4 sm:px-6 pt-3 sm:pt-0">
              <div className="flex items-center gap-3 sm:hidden">
                <span className="text-[11px] tabular-nums text-neutral-400 w-9">
                  {fmt(progress)}
                </span>
                <input
                  type="range"
                  className="afr-range flex-1"
                  min={0}
                  max={1}
                  step={0.001}
                  value={ratio}
                  onChange={(e) => seek(parseFloat(e.target.value))}
                  data-testid="seek-slider-mobile"
                  aria-label="Seek"
                />
                <span className="text-[11px] tabular-nums text-neutral-400 w-9 text-right">
                  {fmt(duration)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3">
              {/* metadata */}
              <div className="flex items-center gap-3 min-w-0 flex-1 sm:flex-none sm:w-56">
                <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl">
                  <img
                    src={current.cover}
                    alt={current.title}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p
                    data-testid="dock-title"
                    className="font-heading text-sm font-semibold tracking-tight truncate"
                  >
                    {current.title}
                  </p>
                  <p className="text-xs text-neutral-500 truncate">{current.artist}</p>
                </div>
              </div>

              {/* controls */}
              <div className="flex items-center gap-1 sm:gap-2 sm:flex-1 sm:justify-center">
                <button
                  data-testid="dock-prev"
                  onClick={prev}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-600 hover:bg-neutral-100 hover:text-black transition-colors duration-200"
                  aria-label="Previous"
                >
                  <SkipBack size={17} className="fill-current" />
                </button>
                <button
                  data-testid="play-pause-btn"
                  onClick={togglePlay}
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-black text-white transition-transform duration-200 hover:scale-105 active:scale-95"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <Pause size={18} className="fill-current" />
                  ) : (
                    <Play size={18} className="ml-0.5 fill-current" />
                  )}
                </button>
                <button
                  data-testid="dock-next"
                  onClick={next}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-600 hover:bg-neutral-100 hover:text-black transition-colors duration-200"
                  aria-label="Next"
                >
                  <SkipForward size={17} className="fill-current" />
                </button>
              </div>

              {/* desktop seek + volume */}
              <div className="hidden sm:flex items-center gap-3 flex-1">
                <span className="text-[11px] tabular-nums text-neutral-400 w-9">
                  {fmt(progress)}
                </span>
                <input
                  type="range"
                  className="afr-range flex-1"
                  min={0}
                  max={1}
                  step={0.001}
                  value={ratio}
                  onChange={(e) => seek(parseFloat(e.target.value))}
                  data-testid="seek-slider"
                  aria-label="Seek"
                />
                <span className="text-[11px] tabular-nums text-neutral-400 w-9">
                  {fmt(duration)}
                </span>
              </div>

              <div className="hidden lg:flex items-center gap-2 w-28">
                <Volume2 size={16} className="text-neutral-500 shrink-0" />
                <input
                  type="range"
                  className="afr-range flex-1"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  data-testid="volume-slider"
                  aria-label="Volume"
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AudioDock;
